import React, { useCallback, useEffect, useMemo, useState, useRef, lazy, createElement } from 'react';
import { Terminal, Cpu, CheckCircle2, DollarSign, Zap, ChevronRight, Loader2, Network, MessageCircleIcon, LayoutIcon, RocketIcon, ClockIcon, SearchIcon, ScissorsIcon, MapPinIcon, GlobeIcon, SparklesIcon } from 'lucide-react';
// ── ROBOTIC VOICE SYSTEM (iOS + Android compatible) ──
// iOS CRITICAL: speechSynthesis.speak() must originate from a user gesture.
// Strategy: "Unlock" speech on the first tap, then queue utterances naturally.
// On iOS we NEVER call cancel() between utterances (it re-locks speech).
let _robotVoice: SpeechSynthesisVoice | null = null;
let _voicesLoaded = false;
let _iosKeepAlive: ReturnType<typeof setInterval> | null = null;
let _iosSpeechUnlocked = false;
const _isIOS = typeof navigator !== 'undefined' && /iP(hone|ad|od)/i.test(navigator.userAgent);
function loadVoices() {
  const voices = window.speechSynthesis?.getVoices() || [];
  if (voices.length === 0) return;
  _voicesLoaded = true;
  const spanishVoices = voices.filter(v => /es[-_]/i.test(v.lang));
  // iOS: Paulina (es-MX) — clear and works well with slight pitch shift
  const iosFemale = spanishVoices.find(v => /paulina|mónica|monica/i.test(v.name));
  // Google español — clear synthetic voice, good on Chrome desktop
  const googleVoice = spanishVoices.find(v => /google/i.test(v.name));
  // Microsoft offline/compact voices — synthetic but clear
  const msOffline = spanishVoices.find(v => /microsoft/i.test(v.name) && !/online|natural/i.test(v.name));
  const msAny = spanishVoices.find(v => /microsoft/i.test(v.name));
  const anySpanish = spanishVoices[0] || null;
  // Priority: iOS Paulina → Google → MS offline → MS any → any Spanish
  _robotVoice = iosFemale || googleVoice || msOffline || msAny || anySpanish || voices[0] || null;
}
// Load voices — with iOS polling fallback
if (typeof window !== 'undefined' && window.speechSynthesis) {
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
  if (!_voicesLoaded) {
    let attempts = 0;
    const poll = setInterval(() => {
      loadVoices();
      attempts++;
      if (_voicesLoaded || attempts > 20) clearInterval(poll);
    }, 250);
  }
}
// iOS: Unlock speech synthesis from a user gesture context.
// Must be called synchronously inside a click/tap handler.
function unlockIOSSpeech() {
  if (!_isIOS || _iosSpeechUnlocked) return;
  try {
    loadVoices();
    // Speak a silent utterance to unlock the speech engine
    const utter = new SpeechSynthesisUtterance(' ');
    utter.volume = 0.01; // near-silent (0 may be ignored on some iOS versions)
    utter.rate = 10; // fastest possible — finishes instantly
    utter.lang = 'es-MX';
    if (_robotVoice) utter.voice = _robotVoice;
    window.speechSynthesis.speak(utter);
    _iosSpeechUnlocked = true;
  } catch {}
  // Also unlock AudioContext for key clicks
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
  } catch {}
}
// iOS: Listen for ANY user tap to keep speech unlocked.
// This ensures speech works even after cancel() re-locks it.
if (_isIOS && typeof document !== 'undefined') {
  const _iosGlobalUnlock = () => {
    unlockIOSSpeech();
  };
  document.addEventListener('touchstart', _iosGlobalUnlock, {
    passive: true
  });
  document.addEventListener('click', _iosGlobalUnlock, {
    passive: true
  });
}
// iOS keep-alive: prevent auto-pause after ~15s
function startIOSKeepAlive() {
  if (!_isIOS || _iosKeepAlive) return;
  _iosKeepAlive = setInterval(() => {
    try {
      if (window.speechSynthesis?.speaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    } catch {}
  }, 5000);
}
function stopIOSKeepAlive() {
  if (_iosKeepAlive) {
    clearInterval(_iosKeepAlive);
    _iosKeepAlive = null;
  }
}
// ── ROBOTIC VOICE PARAMETERS ──
// Slightly elevated pitch + steady rate = robotic but clear and understandable
const SPEECH_RATE = 1.0;
const SPEECH_PITCH = 1.4;
const CHARS_PER_SEC = 14 * SPEECH_RATE;
const SENTENCE_PAUSE_MS = 200;
// Convert number to Spanish words (for TTS)
function numberToSpanishWords(n: number): string {
  if (n === 0) return 'cero';
  const units = ['', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
  const teens = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
  const tens = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
  const hundreds = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
  function below1000(x: number): string {
    if (x === 100) return 'cien';
    let result = '';
    if (x >= 100) {
      result += hundreds[Math.floor(x / 100)] + ' ';
      x %= 100;
    }
    if (x >= 20) {
      result += tens[Math.floor(x / 10)];
      if (x % 10 > 0) result += ' y ' + units[x % 10];
    } else if (x >= 10) {
      result += teens[x - 10];
    } else if (x > 0) {
      result += units[x];
    }
    return result.trim();
  }
  if (n >= 1000000) {
    const millions = Math.floor(n / 1000000);
    const rest = n % 1000000;
    const prefix = millions === 1 ? 'un millón' : below1000(millions) + ' millones';
    return rest > 0 ? prefix + ' ' + numberToSpanishWords(rest) : prefix;
  }
  if (n >= 1000) {
    const thousands = Math.floor(n / 1000);
    const rest = n % 1000;
    const prefix = thousands === 1 ? 'mil' : below1000(thousands) + ' mil';
    return rest > 0 ? prefix + ' ' + below1000(rest) : prefix;
  }
  return below1000(n);
}
function cleanTextForSpeech(text: string): string {
  return text.replace(/^>\s*/g, '').replace(/[™©®]/g, '').replace(/landing\s+pages?/gi, 'lánding péich').replace(/\s*[–—-]\s*/g, ' a ')
  // Convert Colombian peso values ($XXX.XXX) to spoken Spanish words
  .replace(/\$(\d{1,3}(?:\.\d{3})*)/g, (_match, numStr) => {
    const num = parseInt(numStr.replace(/\./g, ''), 10);
    if (isNaN(num)) return _match;
    return numberToSpanishWords(num) + ' pesos';
  }).replace(/\n+/g, '. ').replace(/\.{2,}/g, '.').trim();
}
// Estimate how long the voice will take to read the text (in ms)
function estimateSpeechDurationMs(text: string): number {
  const clean = cleanTextForSpeech(text);
  if (!clean) return 1000;
  const chunks = clean.match(/[^.!?]+[.!?]?/g) || [clean];
  const totalChars = chunks.reduce((sum, c) => sum + c.trim().length, 0);
  const speakingTime = totalChars / CHARS_PER_SEC * 1000;
  const pauseTime = Math.max(0, chunks.length - 1) * SENTENCE_PAUSE_MS;
  return speakingTime + pauseTime;
}
// Calculate typewriter ms-per-character to match voice duration
function calcTypewriterSpeed(text: string): number {
  const durationMs = estimateSpeechDurationMs(text);
  // Divide total duration by number of characters in original text
  const msPerChar = durationMs / Math.max(text.length, 1);
  // Clamp between 20ms and 150ms per char for readability
  return Math.max(20, Math.min(150, msPerChar));
}
// ── Sequential speech queue — speaks ONE utterance at a time ──
// Key: cancel browser queue before each speak() so only 1 utterance exists at a time.
const _spokenTexts = new Set<string>();
const _speechQueue: string[] = [];
let _isSpeaking = false;
let _cancelled = false;
let _safetyTimer: ReturnType<typeof setTimeout> | null = null;
function _clearSafetyTimer() {
  if (_safetyTimer) {
    clearTimeout(_safetyTimer);
    _safetyTimer = null;
  }
}
function _advanceQueue() {
  _clearSafetyTimer();
  _isSpeaking = false;
  stopIOSKeepAlive();
  setTimeout(() => _processQueue(), 120);
}
function _processQueue() {
  if (_cancelled || _isSpeaking || _speechQueue.length === 0) return;
  _isSpeaking = true;
  const clean = _speechQueue.shift()!;
  // iOS path: DON'T cancel — just queue directly (cancel re-locks speech on iOS)
  if (_isIOS) {
    try {
      try {
        window.speechSynthesis.resume();
      } catch {}
      const utter = new SpeechSynthesisUtterance(clean);
      utter.lang = 'es-MX';
      utter.rate = SPEECH_RATE;
      utter.pitch = SPEECH_PITCH;
      utter.volume = 1.0;
      if (_robotVoice) utter.voice = _robotVoice;
      utter.onend = () => _advanceQueue();
      utter.onerror = () => _advanceQueue();
      utter.onstart = () => startIOSKeepAlive();
      window.speechSynthesis.speak(utter);
      // iOS safety timeout (onend can be unreliable)
      const estimatedMs = clean.length / CHARS_PER_SEC * 1000 + 4000;
      _clearSafetyTimer();
      _safetyTimer = setTimeout(() => {
        if (_isSpeaking) _advanceQueue();
      }, estimatedMs);
    } catch {
      _advanceQueue();
    }
    return;
  }
  // Non-iOS path: cancel browser queue first, then speak after short delay
  try {
    try {
      window.speechSynthesis.cancel();
    } catch {}
    setTimeout(() => {
      if (_cancelled) {
        _isSpeaking = false;
        return;
      }
      try {
        const utter = new SpeechSynthesisUtterance(clean);
        utter.lang = 'es-MX';
        utter.rate = SPEECH_RATE;
        utter.pitch = SPEECH_PITCH;
        utter.volume = 1.0;
        if (_robotVoice) utter.voice = _robotVoice;
        utter.onend = () => _advanceQueue();
        utter.onerror = () => _advanceQueue();
        window.speechSynthesis.speak(utter);
        const estimatedMs = clean.length / CHARS_PER_SEC * 1000 + 3000;
        _clearSafetyTimer();
        _safetyTimer = setTimeout(() => {
          if (_isSpeaking) {
            try {
              window.speechSynthesis.cancel();
            } catch {}
            _advanceQueue();
          }
        }, estimatedMs);
      } catch {
        _advanceQueue();
      }
    }, 80);
  } catch {
    _isSpeaking = false;
    setTimeout(() => _processQueue(), 120);
  }
}
// Speak text — adds to sequential queue, deduplicates
function speakRobotic(text: string) {
  try {
    if (!window.speechSynthesis) return;
    const clean = cleanTextForSpeech(text);
    if (!clean) return;
    // Deduplicate: never speak the same text twice within a step
    if (_spokenTexts.has(clean)) return;
    _spokenTexts.add(clean);
    loadVoices();
    _cancelled = false;
    _speechQueue.push(clean);
    _processQueue();
  } catch {
    // Speech not available — silently fail
  }
}
function cancelSpeech() {
  try {
    _cancelled = true;
    _speechQueue.length = 0;
    _isSpeaking = false;
    _clearSafetyTimer();
    stopIOSKeepAlive();
    _spokenTexts.clear();
    if (_isIOS) {
      // On iOS, cancel() re-locks speech synthesis.
      // Mark as needing re-unlock — the global touchstart/click listener
      // will re-unlock it on the next user tap.
      window.speechSynthesis?.cancel();
      _iosSpeechUnlocked = false;
    } else {
      window.speechSynthesis?.cancel();
    }
  } catch {}
}
// ── Keyboard click sound (Web Audio API) ──
let _audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext | null {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  } catch {
    return null;
  }
}
function playKeyClick() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const duration = 0.03;
    const now = ctx.currentTime;
    // White noise burst → sounds like a mechanical key click
    const bufferSize = Math.ceil(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.3;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    // Bandpass filter to shape the click (keyboard-like)
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 4000 + Math.random() * 2000;
    filter.Q.value = 1.5;
    // Gain envelope — quick attack, fast decay
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start(now);
    noise.stop(now + duration);
  } catch {
    // Audio not available
  }
}
// ── Typewriter component (synced to voice speed) ──
// IMPORTANT: Does NOT cancel speech on mount/unmount. Speech flows naturally
// in queue. Only cancelSpeech() in handleStep (step transitions) stops it.
function TypewriterText({
  text,
  speed,
  onComplete,
  className = '',
  speak = true
}: {
  text: string;
  speed?: number;
  onComplete?: () => void;
  className?: string;
  speak?: boolean;
}) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const hasStartedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const syncedSpeed = useMemo(() => calcTypewriterSpeed(text), [text]);
  useEffect(() => {
    // Strict guard: only run animation once per component instance
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    if (speak) speakRobotic(text);
    let idx = 0;
    const interval = setInterval(() => {
      idx += 1;
      if (idx >= text.length) {
        setDisplayed(text);
        setDone(true);
        clearInterval(interval);
        onCompleteRef.current?.();
      } else {
        setDisplayed(text.slice(0, idx));
        const ch = text[idx - 1];
        if (ch && ch !== ' ' && ch !== '\n') playKeyClick();
      }
    }, syncedSpeed);
    return () => clearInterval(interval);
  }, [text, syncedSpeed, speak]);
  return <span className={className} data-id="element-605">
      {displayed}
      {!done && <span className="inline-block w-[2px] h-[1em] bg-blue-400 ml-[1px] animate-pulse align-middle" data-id="element-606" />}
    </span>;
}
// ── Typewriter for system log lines (synced to voice speed) ──
function TypewriterLog({
  text,
  speed,
  onComplete,
  className = '',
  speak = true
}: {
  text: string;
  speed?: number;
  onComplete?: () => void;
  className?: string;
  speak?: boolean;
}) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const hasStartedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const syncedSpeed = useMemo(() => calcTypewriterSpeed(text), [text]);
  useEffect(() => {
    // Strict guard: only run animation once per component instance
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    if (speak) speakRobotic(text);
    let idx = 0;
    const interval = setInterval(() => {
      idx += 1;
      if (idx >= text.length) {
        setDisplayed(text);
        setDone(true);
        clearInterval(interval);
        onCompleteRef.current?.();
      } else {
        setDisplayed(text.slice(0, idx));
        const ch = text[idx - 1];
        if (ch && ch !== ' ' && ch !== '\n') playKeyClick();
      }
    }, syncedSpeed);
    return () => clearInterval(interval);
  }, [text, syncedSpeed, speak]);
  return <p className={className} data-id="element-607">
      {displayed}
      {!done && <span className="inline-block w-[2px] h-[1em] bg-blue-400 ml-[1px] animate-pulse align-middle" data-id="element-608" />}
    </p>;
}
export function Dinero1Page() {
  // Allow skipping to any step via URL param: ?step=10
  const initialStep = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const s = parseInt(params.get('step') || '1', 10);
      return s >= 1 && s <= 10 ? s : 1;
    } catch {
      return 1;
    }
  })();
  const [step, setStep] = useState(initialStep);
  // ── Meta Pixel — deferred load ──
  useEffect(() => {
    const loadPixel = () => {
      if ((window as any).fbq) return; // already loaded
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '3188173048187250');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
      const noscript = document.createElement('noscript');
      noscript.innerHTML = '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=3188173048187250&ev=PageView&noscript=1" />';
      document.body.appendChild(noscript);
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadPixel, 2000);
    }
  }, []);
  const handleStep = useCallback((s: number) => {
    cancelSpeech();
    // iOS: re-unlock speech AFTER cancel (cancel re-locks it)
    // Must happen synchronously in this tap handler
    unlockIOSSpeech();
    setStep(s);
  }, []);
  return <div className="min-h-screen text-blue-50 font-mono selection:bg-blue-900 selection:text-blue-100 flex flex-col items-center p-4 overflow-hidden relative" style={{
    backgroundColor: '#0f172a'
  }} data-id="element-609">
      {/* Background effects */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-610">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(29,78,216,0.15)_0,rgba(15,23,42,1)_100%)]" data-id="element-611" />
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-612" />
      </div>

      {/* Fixed header - only shows from step 2 onwards */}
      {step >= 2 && <div className="relative z-20 w-full max-w-2xl flex items-center gap-3 py-4 mb-4 border-b border-blue-900/50 animate-fade-in" data-id="element-613">
          <div className="w-12 h-12 rounded-full bg-blue-900/40 border border-blue-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-id="element-614">
            <Terminal className="w-6 h-6 text-blue-400" data-id="element-615" />
          </div>
          <div data-id="element-616">
            <h2 className="text-xl font-bold text-blue-100 tracking-wider" data-id="element-617">
              LPM™ AI Core
            </h2>
            <p className="text-xs text-green-400 flex items-center gap-2" data-id="element-618">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" data-id="element-619" />
              Sistema en línea
            </p>
          </div>
        </div>}

      <div className="relative z-10 w-full max-w-2xl flex-1 flex flex-col justify-center" data-id="element-620">
        {step === 1 && <Step1Activation onComplete={() => handleStep(2)} data-id="element-621" />}
        {step === 2 && <Step2Transition onComplete={() => handleStep(3)} data-id="element-622" />}
        {step === 3 && <Step3Diagnostic onComplete={() => handleStep(4)} data-id="element-623" />}
        {step === 4 && <Step4Processing onComplete={() => handleStep(5)} data-id="element-624" />}
        {step === 5 && <Step5Result onComplete={() => handleStep(6)} data-id="element-625" />}
        {step === 6 && <Step6HowItWorks onComplete={() => handleStep(7)} data-id="element-626" />}
        {step === 7 && <Step7Simulator onComplete={() => handleStep(8)} data-id="element-627" />}
        {step === 8 && <Step8ClientSim onComplete={() => handleStep(9)} data-id="element-628" />}
        {step === 9 && <Step9Testimonials onComplete={() => handleStep(10)} data-id="element-629" />}
        {step === 10 && <Step10Offer data-id="element-630" />}
      </div>
    </div>;
}
function Step1Activation({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const [activated, setActivated] = useState(false);
  const [log1Done, setLog1Done] = useState(false);
  const [log2Done, setLog2Done] = useState(false);
  const [log3Done, setLog3Done] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  // User must tap "ACTIVAR" first — this unlocks speech + audio on mobile
  const handleActivate = () => {
    // Unlock speech synthesis (iOS requires user gesture)
    unlockIOSSpeech();
    // Unlock AudioContext for key click sounds (all mobile browsers)
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume();
    } catch {}
    setActivated(true);
    // Start log sequence after a short delay
    setTimeout(() => setPhase(1), 800);
  };
  // If not on mobile, auto-activate after delay (desktop doesn't need gesture)
  useEffect(() => {
    if (!_isIOS && !/Android/i.test(navigator.userAgent || '')) {
      // Desktop: auto-start after delay
      const t1 = setTimeout(() => {
        setActivated(true);
        setTimeout(() => setPhase(1), 800);
      }, 1500);
      return () => clearTimeout(t1);
    }
  }, []);
  useEffect(() => {
    if (log1Done && phase === 1) {
      const t = setTimeout(() => setPhase(2), 300);
      return () => clearTimeout(t);
    }
  }, [log1Done, phase]);
  useEffect(() => {
    if (log2Done && phase === 2) {
      const t = setTimeout(() => setPhase(3), 300);
      return () => clearTimeout(t);
    }
  }, [log2Done, phase]);
  useEffect(() => {
    if (log3Done && phase === 3) {
      const t = setTimeout(() => setPhase(4), 800);
      return () => clearTimeout(t);
    }
  }, [log3Done, phase]);
  // Auto-scroll down as new content appears so user always sees the latest
  useEffect(() => {
    if (phase >= 1) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
      }, 200);
    }
  }, [phase, log1Done, log2Done, log3Done]);
  return <div className="flex flex-col items-center min-h-[85vh] relative overflow-hidden" data-id="element-631">
      {/* === EYES (laser source from user's perspective) === */}
      <div className="relative flex items-center justify-center gap-10 mt-6 z-30 animate-[eyeGlint_4s_ease-in-out_infinite]" data-id="element-632">
        {/* Left eye */}
        <div className="relative" data-id="element-633">
          <div className="w-4 h-4 rounded-full bg-cyan-300 animate-[eyePulse_2s_ease-in-out_infinite]" data-id="element-634" />
          <div className="absolute inset-0 w-4 h-4 rounded-full bg-cyan-200 blur-sm animate-pulse" data-id="element-635" />
        </div>
        {/* Right eye */}
        <div className="relative" data-id="element-636">
          <div className="w-4 h-4 rounded-full bg-cyan-300 animate-[eyePulse_2s_ease-in-out_infinite_0.3s]" data-id="element-637" />
          <div className="absolute inset-0 w-4 h-4 rounded-full bg-cyan-200 blur-sm animate-pulse" data-id="element-638" />
        </div>
      </div>

      {/* === CONVERGING BEAMS from eyes === */}
      <div className="relative w-full flex justify-center z-20" style={{
      height: '80px'
    }} data-id="element-639">
        {/* Left beam */}
        <div className="absolute" style={{
        left: 'calc(50% - 20px - 18px)',
        top: 0,
        width: '2px',
        height: '80px',
        background: 'linear-gradient(to bottom, rgba(34,211,238,0.7), rgba(34,211,238,0.2))',
        boxShadow: '0 0 8px 2px rgba(34,211,238,0.3)',
        transform: 'rotate(8deg)',
        transformOrigin: 'top center',
        animation: 'beamDescend 1.5s ease-out forwards'
      }} data-id="element-640" />
        {/* Right beam */}
        <div className="absolute" style={{
        left: 'calc(50% + 20px + 16px)',
        top: 0,
        width: '2px',
        height: '80px',
        background: 'linear-gradient(to bottom, rgba(34,211,238,0.7), rgba(34,211,238,0.2))',
        boxShadow: '0 0 8px 2px rgba(34,211,238,0.3)',
        transform: 'rotate(-8deg)',
        transformOrigin: 'top center',
        animation: 'beamDescend 1.5s ease-out forwards'
      }} data-id="element-641" />
        {/* Convergence point glow */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-3 h-3 rounded-full bg-cyan-400 blur-[3px]" style={{
        animation: 'fadeIn 1.5s ease-out forwards',
        opacity: 0
      }} data-id="element-642" />
      </div>

      {/* === EXPANDING BEAM CONE downward === */}
      <div className="relative w-full flex justify-center z-10" style={{
      height: '40px'
    }} data-id="element-643">
        <div style={{
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '40px solid rgba(34,211,238,0.12)',
        filter: 'blur(4px)',
        animation: 'fadeIn 1.8s ease-out forwards',
        opacity: 0
      }} data-id="element-644" />
        {/* Wider ambient cone */}
        <div className="absolute left-1/2 -translate-x-1/2 top-0" style={{
        width: 0,
        height: 0,
        borderLeft: '160px solid transparent',
        borderRight: '160px solid transparent',
        borderTop: '50px solid rgba(34,211,238,0.03)',
        filter: 'blur(12px)',
        animation: 'fadeIn 2s ease-out forwards',
        opacity: 0
      }} data-id="element-645" />
      </div>

      {/* === HOLOGRAPHIC PROJECTED CONTENT === */}
      <div className="relative w-full max-w-md holo-flicker" style={{
      animation: 'contentReveal 2s ease-out 0.8s forwards',
      clipPath: 'inset(0 0 100% 0)',
      opacity: 0
    }} data-id="element-646">
        {/* Holographic scan lines overlay — BELOW content */}
        <div className="absolute inset-0 holo-scanline pointer-events-none z-[1] rounded-2xl" data-id="element-647" />

        {/* Vertical scanning beam — BELOW content */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none z-[1]" data-id="element-648">
          <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" style={{
          animation: 'holoScan 3s linear infinite'
        }} data-id="element-649" />
        </div>

        {/* Floating particles — BELOW content */}
        <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden rounded-2xl" data-id="element-650">
          {[...Array(10)].map((_, i) => <div key={i} className="absolute w-[2px] h-[2px] rounded-full bg-cyan-400/50" style={{
          left: `${10 + Math.random() * 80}%`,
          bottom: `${Math.random() * 40}%`,
          animation: `particleFloat ${2 + Math.random() * 3}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 4}s`
        }} data-id="element-651" />)}
        </div>

        {/* Holographic border glow */}
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-cyan-400/40 via-cyan-400/10 to-transparent blur-[2px] pointer-events-none" data-id="element-652" />

        {/* Main projected content panel — ABOVE overlays */}
        <div className="relative bg-black/50 border border-cyan-500/25 rounded-2xl p-8 backdrop-blur-md space-y-6 overflow-hidden z-[5]" style={{
        boxShadow: '0 0 80px rgba(34,211,238,0.12), inset 0 0 50px rgba(34,211,238,0.05)'
      }} data-id="element-653">
          {/* Top beam impact glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-24 bg-cyan-400/15 blur-3xl pointer-events-none" data-id="element-654" />

          {/* Dollar icon */}
          <div className="relative w-20 h-20 mx-auto" data-id="element-655">
            <div className="absolute inset-0 rounded-full border border-cyan-400/30 animate-[spin_8s_linear_infinite]" data-id="element-656">
              <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-[0_0_8px_rgba(34,211,238,0.9)]" data-id="element-657" />
            </div>
            <div className="absolute inset-2 rounded-full border border-cyan-500/20 animate-[spin_5s_linear_infinite_reverse]" data-id="element-658">
              <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.7)]" data-id="element-659" />
            </div>
            <div className="absolute inset-4 rounded-full bg-gradient-to-br from-cyan-950/80 to-black/80 border border-cyan-400/40 flex items-center justify-center shadow-[0_0_30px_rgba(34,211,238,0.25)]" data-id="element-660">
              <DollarSign className="w-7 h-7 text-cyan-300 drop-shadow-[0_0_15px_rgba(34,211,238,1)]" data-id="element-661" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold tracking-[0.2em] text-center text-cyan-200 drop-shadow-[0_0_20px_rgba(34,211,238,0.6)]" style={{
          textShadow: '0 0 30px rgba(34,211,238,0.4), 0 0 60px rgba(34,211,238,0.2)'
        }} data-id="element-662">
            SISTEMA LPM™
          </h1>
          <p className="text-xs text-center tracking-[0.3em] text-cyan-400/60 uppercase font-bold" data-id="element-663">
            Inicializando protocolo
          </p>

          {/* Progress beam */}
          <div className="w-full h-[3px] bg-cyan-950/50 rounded-full overflow-hidden relative" data-id="element-664">
            {activated ? <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/80 via-cyan-300 to-cyan-500/80 animate-[scan_2.5s_ease-in-out_infinite]" style={{
            boxShadow: '0 0 10px rgba(34,211,238,0.6)'
          }} data-id="element-665" /> : <div className="absolute inset-0 bg-cyan-800/30" data-id="element-666" />}
          </div>

          {/* Mobile: "ACTIVAR" button — must tap to unlock speech + audio */}
          {!activated && <div className="space-y-4 animate-fade-in" data-id="element-667">
              <p className="text-center text-cyan-300/60 text-xs tracking-widest uppercase" data-id="element-668">
                Toca para iniciar el análisis
              </p>
              <button onClick={handleActivate} className="group relative w-full py-5 rounded-xl overflow-hidden transition-all border border-cyan-500/50 hover:border-cyan-400/70 active:scale-[0.98]" style={{
            boxShadow: '0 0 30px rgba(34,211,238,0.2)'
          }} data-id="element-669">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/40 via-cyan-800/30 to-cyan-900/40 group-hover:from-cyan-800/50 group-hover:to-cyan-800/50 transition-all" data-id="element-670" />
                <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(34,211,238,0.15)_50%,transparent_75%)] bg-[length:200%_200%] animate-[slide_3s_linear_infinite]" data-id="element-671" />
                <span className="relative flex items-center justify-center gap-3 text-cyan-200 font-bold tracking-[0.2em] text-lg drop-shadow-[0_0_10px_rgba(34,211,238,0.6)]" data-id="element-672">
                  <Zap className="w-5 h-5 animate-pulse" data-id="element-673" />
                  ACTIVAR SISTEMA
                </span>
              </button>
            </div>}

          {/* Log lines — voice + keyboard sound enabled (only after activation) */}
          {activated && <div className="space-y-2 text-sm text-cyan-300/80 font-mono min-h-[120px]" data-id="element-674">
              {phase >= 1 && <TypewriterLog text="> Analizando capacidades del usuario..." speed={60} speak={true} onComplete={() => setLog1Done(true)} className="drop-shadow-[0_0_4px_rgba(34,211,238,0.5)]" data-id="element-675" />}
              {phase >= 2 && <TypewriterLog text="> Detectando herramientas disponibles..." speed={60} speak={true} onComplete={() => setLog2Done(true)} className="drop-shadow-[0_0_4px_rgba(34,211,238,0.5)]" data-id="element-676" />}
              {phase >= 3 && <TypewriterLog text="> Evaluando potencial de ingresos..." speed={60} speak={true} onComplete={() => setLog3Done(true)} className="drop-shadow-[0_0_4px_rgba(34,211,238,0.5)]" data-id="element-677" />}
              {phase >= 4 && <TypewriterLog text="> Diagnóstico listo." speed={70} speak={true} className="text-green-400 font-bold mt-2 drop-shadow-[0_0_8px_rgba(74,222,128,0.6)]" data-id="element-678" />}
            </div>}

          {/* CTA Button */}
          {phase >= 4 && <div className="animate-fade-in space-y-3" data-id="element-679">
              <p className="text-center text-cyan-200/80 text-sm md:text-base leading-relaxed" data-id="element-680">
                <TypewriterText text="¿Tienes lo necesario para generar entre 2 y 6 millones al mes?" speak={true} className="text-cyan-200/80" data-id="element-681" />
              </p>
              <button onClick={onComplete} className="group relative w-full py-4 rounded-xl overflow-hidden transition-all border border-cyan-500/40 hover:border-cyan-400/70" style={{
            boxShadow: '0 0 25px rgba(34,211,238,0.15)'
          }} data-id="element-682">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/30 via-cyan-800/20 to-cyan-900/30 group-hover:from-cyan-800/40 group-hover:to-cyan-800/40 transition-all" data-id="element-683" />
                <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(34,211,238,0.1)_50%,transparent_75%)] bg-[length:200%_200%] animate-[slide_3s_linear_infinite]" data-id="element-684" />
                <span className="relative flex items-center justify-center gap-2 text-cyan-200 font-bold tracking-wider drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]" data-id="element-685">
                  INICIAR DIAGNÓSTICO
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-686" />
                </span>
              </button>
            </div>}
        </div>

        {/* Bottom reflection on "floor" */}
        <div className="w-3/4 mx-auto h-10 bg-gradient-to-b from-cyan-400/10 to-transparent blur-xl mt-1" data-id="element-687" />
      </div>

      {/* Scroll anchor — auto-scroll targets this */}
      <div ref={bottomRef} data-id="element-688" />
    </div>;
}
function Step2Transition({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const [log1Done, setLog1Done] = useState(false);
  const [log2Done, setLog2Done] = useState(false);
  const [log3Done, setLog3Done] = useState(false);
  const [log4Done, setLog4Done] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 800);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (log1Done && phase === 1) {
      const t = setTimeout(() => setPhase(2), 200);
      return () => clearTimeout(t);
    }
  }, [log1Done, phase]);
  useEffect(() => {
    if (log2Done && phase === 2) {
      const t = setTimeout(() => setPhase(3), 200);
      return () => clearTimeout(t);
    }
  }, [log2Done, phase]);
  useEffect(() => {
    if (log3Done && phase === 3) {
      const t = setTimeout(() => setPhase(4), 200);
      return () => clearTimeout(t);
    }
  }, [log3Done, phase]);
  useEffect(() => {
    if (log4Done && phase === 4) {
      const t = setTimeout(() => setPhase(5), 400);
      return () => clearTimeout(t);
    }
  }, [log4Done, phase]);
  useEffect(() => {
    if (phase === 5) {
      // Wait long enough for the speech queue to finish all 5 log lines
      // Each line ~2-3s of speech; by phase 5, earlier lines may still be playing
      const t = setTimeout(() => onComplete(), 6000);
      return () => clearTimeout(t);
    }
  }, [phase, onComplete]);
  return <div className="flex flex-col items-center justify-center space-y-8 min-h-[60vh] animate-fade-in" data-id="element-689">
      <div className="text-center space-y-6" data-id="element-690">
        <Network className="w-16 h-16 text-purple-500 mx-auto animate-pulse drop-shadow-[0_0_20px_rgba(168,85,247,0.6)]" data-id="element-691" />
        <h2 className="text-xl md:text-3xl font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 animate-pulse" data-id="element-692">
          CONECTANDO CON MOTOR DE DIAGNÓSTICO LPM™
        </h2>
      </div>

      <div className="w-full max-w-md h-2 bg-gray-900 rounded-full overflow-hidden relative shadow-[0_0_15px_rgba(59,130,246,0.2)] border border-blue-900/50" data-id="element-693">
        <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-600 to-purple-500 transition-all duration-1000 ease-out" style={{
        width: `${phase / 5 * 100}%`
      }} data-id="element-694">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] bg-[length:20px_20px] animate-[slide_1s_linear_infinite]" data-id="element-695" />
        </div>
      </div>

      <div className="space-y-3 text-sm md:text-base text-blue-300 font-mono h-48 flex flex-col w-full max-w-md bg-blue-950/20 p-6 rounded-xl border border-blue-900/30 backdrop-blur-sm" data-id="element-696">
        {phase >= 1 && <TypewriterLog text="> Conectando con servidor de análisis..." speed={50} onComplete={() => setLog1Done(true)} data-id="element-697" />}
        {phase >= 2 && <TypewriterLog text="> Escaneando perfil del usuario..." speed={50} onComplete={() => setLog2Done(true)} data-id="element-698" />}
        {phase >= 3 && <TypewriterLog text="> Detectando herramientas disponibles..." speed={50} onComplete={() => setLog3Done(true)} data-id="element-699" />}
        {phase >= 4 && <TypewriterLog text="> Evaluando habilidades digitales básicas..." speed={50} onComplete={() => setLog4Done(true)} data-id="element-700" />}
        {phase >= 5 && <TypewriterLog text="> Conexión establecida." speed={60} className="text-green-400 font-bold mt-2 drop-shadow-[0_0_8px_rgba(74,222,128,0.5)]" data-id="element-701" />}
      </div>
    </div>;
}
function Step3Diagnostic({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [chatPhase, setChatPhase] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [introTyped, setIntroTyped] = useState(false);
  const [q1Typed, setQ1Typed] = useState(false);
  const [q2Typed, setQ2Typed] = useState(false);
  const [q3Typed, setQ3Typed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [chatPhase, answers, introTyped, q1Typed, q2Typed, q3Typed]);
  const handleContinue = () => {
    setAnswers([...answers, 'Continuar']);
    setChatPhase(1);
  };
  const handleAnswer = (answer: string) => {
    setAnswers([...answers, answer]);
    if (chatPhase === 1) setChatPhase(2);else if (chatPhase === 2) setChatPhase(3);else if (chatPhase === 3) {
      setChatPhase(4);
      setTimeout(() => onComplete(), 1000);
    }
  };
  const introText = 'Sistema LPM™ conectado.\n\nVoy a analizar si tienes lo necesario para generar ingresos creando páginas web con inteligencia artificial.\n\nSolo necesito hacerte 3 preguntas rápidas.';
  const q1Text = 'Primero, algo importante.\n\n¿Tienes computador o laptop?';
  const q2Text = 'Perfecto.\n\n¿Sabes copiar y pegar texto en un computador?';
  const q3Text = 'Última pregunta.\n\n¿Tienes acceso a internet estable?';
  return <div className="w-full max-w-2xl mx-auto flex-1 flex flex-col animate-fade-in" data-id="element-702">
      <div className="flex-1 overflow-y-auto space-y-6 pr-2 custom-scrollbar pb-20" data-id="element-703">
        {/* Intro Message */}
        <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-704">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-705">
            <Cpu className="w-4 h-4 text-blue-400" data-id="element-706" />
          </div>
          <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-707">
            <p className="text-blue-100 leading-relaxed whitespace-pre-line" data-id="element-708">
              <TypewriterText text={introText} speed={60} onComplete={() => setIntroTyped(true)} data-id="element-709" />
            </p>
          </div>
        </div>

        {chatPhase === 0 && introTyped && <div className="flex justify-end animate-fade-in-up" data-id="element-710">
            <button onClick={handleContinue} className="px-6 py-3 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/50 rounded-xl text-blue-200 font-bold transition-all shadow-[0_0_15px_rgba(59,130,246,0.2)] hover:shadow-[0_0_25px_rgba(59,130,246,0.4)]" data-id="element-711">
              Continuar
            </button>
          </div>}

        {/* User Answer 0 */}
        {answers[0] && <div className="flex justify-end animate-fade-in-up" data-id="element-712">
            <div className="bg-purple-900/40 border border-purple-500/30 p-3 rounded-2xl rounded-tr-none text-purple-100" data-id="element-713">
              {answers[0]}
            </div>
          </div>}

        {/* Question 1 */}
        {chatPhase >= 1 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-714">
            <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-715">
              <Cpu className="w-4 h-4 text-blue-400" data-id="element-716" />
            </div>
            <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-717">
              <p className="text-blue-100 leading-relaxed whitespace-pre-line" data-id="element-718">
                <TypewriterText text={q1Text} speed={70} onComplete={() => setQ1Typed(true)} data-id="element-719" />
              </p>
            </div>
          </div>}

        {chatPhase === 1 && q1Typed && <div className="flex justify-end gap-3 animate-fade-in-up" data-id="element-720">
            <button onClick={() => handleAnswer('Sí')} className="px-8 py-3 bg-blue-900/40 hover:bg-blue-800/60 border border-blue-600/50 rounded-xl text-blue-100 transition-all" data-id="element-721">
              Sí
            </button>
            <button onClick={() => handleAnswer('No')} className="px-8 py-3 bg-blue-900/40 hover:bg-blue-800/60 border border-blue-600/50 rounded-xl text-blue-100 transition-all" data-id="element-722">
              No
            </button>
          </div>}

        {/* User Answer 1 */}
        {answers[1] && <div className="flex justify-end animate-fade-in-up" data-id="element-723">
            <div className="bg-purple-900/40 border border-purple-500/30 p-3 rounded-2xl rounded-tr-none text-purple-100" data-id="element-724">
              {answers[1]}
            </div>
          </div>}

        {/* Question 2 */}
        {chatPhase >= 2 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-725">
            <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-726">
              <Cpu className="w-4 h-4 text-blue-400" data-id="element-727" />
            </div>
            <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-728">
              <p className="text-blue-100 leading-relaxed whitespace-pre-line" data-id="element-729">
                <TypewriterText text={q2Text} speed={70} onComplete={() => setQ2Typed(true)} data-id="element-730" />
              </p>
            </div>
          </div>}

        {chatPhase === 2 && q2Typed && <div className="flex justify-end gap-3 animate-fade-in-up" data-id="element-731">
            <button onClick={() => handleAnswer('Sí')} className="px-8 py-3 bg-blue-900/40 hover:bg-blue-800/60 border border-blue-600/50 rounded-xl text-blue-100 transition-all" data-id="element-732">
              Sí
            </button>
            <button onClick={() => handleAnswer('No')} className="px-8 py-3 bg-blue-900/40 hover:bg-blue-800/60 border border-blue-600/50 rounded-xl text-blue-100 transition-all" data-id="element-733">
              No
            </button>
          </div>}

        {/* User Answer 2 */}
        {answers[2] && <div className="flex justify-end animate-fade-in-up" data-id="element-734">
            <div className="bg-purple-900/40 border border-purple-500/30 p-3 rounded-2xl rounded-tr-none text-purple-100" data-id="element-735">
              {answers[2]}
            </div>
          </div>}

        {/* Question 3 */}
        {chatPhase >= 3 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-736">
            <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-737">
              <Cpu className="w-4 h-4 text-blue-400" data-id="element-738" />
            </div>
            <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-739">
              <p className="text-blue-100 leading-relaxed whitespace-pre-line" data-id="element-740">
                <TypewriterText text={q3Text} speed={70} onComplete={() => setQ3Typed(true)} data-id="element-741" />
              </p>
            </div>
          </div>}

        {chatPhase === 3 && q3Typed && <div className="flex justify-end gap-3 animate-fade-in-up" data-id="element-742">
            <button onClick={() => handleAnswer('Sí')} className="px-8 py-3 bg-blue-900/40 hover:bg-blue-800/60 border border-blue-600/50 rounded-xl text-blue-100 transition-all" data-id="element-743">
              Sí
            </button>
            <button onClick={() => handleAnswer('No')} className="px-8 py-3 bg-blue-900/40 hover:bg-blue-800/60 border border-blue-600/50 rounded-xl text-blue-100 transition-all" data-id="element-744">
              No
            </button>
          </div>}

        {/* User Answer 3 */}
        {answers[3] && <div className="flex justify-end animate-fade-in-up" data-id="element-745">
            <div className="bg-purple-900/40 border border-purple-500/30 p-3 rounded-2xl rounded-tr-none text-purple-100" data-id="element-746">
              {answers[3]}
            </div>
          </div>}

        <div ref={messagesEndRef} data-id="element-747" />
      </div>
    </div>;
}
function Step4Processing({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const [log1Done, setLog1Done] = useState(false);
  const [log2Done, setLog2Done] = useState(false);
  const [log3Done, setLog3Done] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 1000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (log1Done && phase === 1) {
      const t = setTimeout(() => setPhase(2), 300);
      return () => clearTimeout(t);
    }
  }, [log1Done, phase]);
  useEffect(() => {
    if (log2Done && phase === 2) {
      const t = setTimeout(() => setPhase(3), 300);
      return () => clearTimeout(t);
    }
  }, [log2Done, phase]);
  useEffect(() => {
    if (log3Done && phase === 3) {
      const t = setTimeout(() => onComplete(), 2000);
      return () => clearTimeout(t);
    }
  }, [log3Done, phase, onComplete]);
  return <div className="flex flex-col items-center justify-center space-y-10 min-h-[60vh] animate-fade-in" data-id="element-748">
      <div className="relative w-32 h-32 flex items-center justify-center" data-id="element-749">
        <div className="absolute inset-0 border-t-2 border-blue-500 rounded-full animate-spin shadow-[0_0_15px_rgba(59,130,246,0.5)]" data-id="element-750" />
        <div className="absolute inset-2 border-r-2 border-purple-500 rounded-full animate-[spin_1.5s_linear_infinite_reverse] shadow-[0_0_15px_rgba(168,85,247,0.5)]" data-id="element-751" />
        <div className="absolute inset-4 border-b-2 border-cyan-400 rounded-full animate-[spin_2s_linear_infinite] shadow-[0_0_15px_rgba(34,211,238,0.5)]" data-id="element-752" />
        <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-pulse" data-id="element-753" />
        <Loader2 className="w-10 h-10 text-blue-400 animate-spin" data-id="element-754" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-400 animate-pulse" data-id="element-755">
        ANALIZANDO RESPUESTAS...
      </h2>

      <div className="space-y-4 text-sm md:text-base text-blue-300 font-mono h-32 flex flex-col items-center w-full max-w-lg bg-black/50 p-6 rounded-xl border border-blue-900/30" data-id="element-756">
        {phase >= 1 && <TypewriterLog text="> Evaluando compatibilidad con el modelo LPM™..." speed={50} className="text-center" onComplete={() => setLog1Done(true)} data-id="element-757" />}
        {phase >= 2 && <TypewriterLog text="> Calculando potencial de ingresos..." speed={50} className="text-center" onComplete={() => setLog2Done(true)} data-id="element-758" />}
        {phase >= 3 && <TypewriterLog text="> Generando diagnóstico personalizado..." speed={50} className="text-center text-purple-300 drop-shadow-[0_0_5px_rgba(168,85,247,0.5)]" onComplete={() => setLog3Done(true)} data-id="element-759" />}
      </div>
    </div>;
}
function Step5Result({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(87);
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  return <div className="w-full max-w-lg mx-auto animate-fade-in" data-id="element-760">
      <div className="bg-blue-950/30 border border-blue-500/50 rounded-2xl p-8 shadow-[0_0_50px_rgba(59,130,246,0.2)] backdrop-blur-md relative overflow-hidden" data-id="element-761">
        {/* Glowing top border */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-80 shadow-[0_0_10px_rgba(59,130,246,0.8)]" data-id="element-762" />

        <div className="flex items-center gap-4 mb-8" data-id="element-763">
          <div className="w-12 h-12 rounded-full bg-green-900/30 border border-green-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(74,222,128,0.3)]" data-id="element-764">
            <CheckCircle2 className="w-7 h-7 text-green-400" data-id="element-765" />
          </div>
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-100 to-blue-300 tracking-wide" data-id="element-766">
            RESULTADO DEL DIAGNÓSTICO
          </h2>
        </div>

        <p className="text-blue-100 text-lg leading-relaxed mb-8 font-sans" data-id="element-767">
          Según tus respuestas, tienes{' '}
          <strong className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]" data-id="element-768">
            todo lo necesario
          </strong>{' '}
          para empezar a generar ingresos creando páginas web con inteligencia
          artificial.
        </p>

        <div className="space-y-3 mb-8 bg-black/40 p-5 rounded-xl border border-blue-900/40" data-id="element-769">
          <div className="flex justify-between text-sm text-blue-300 uppercase tracking-wider font-bold" data-id="element-770">
            <span data-id="element-771">Probabilidad estimada de éxito</span>
            <span className="font-mono text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.5)]" data-id="element-772">
              {progress}%
            </span>
          </div>
          <div className="w-full h-4 bg-gray-900 rounded-full overflow-hidden border border-gray-800 shadow-inner" data-id="element-773">
            <div className="h-full bg-gradient-to-r from-blue-600 via-purple-500 to-cyan-400 transition-all duration-[2000ms] ease-out relative" style={{
            width: `${progress}%`
          }} data-id="element-774">
              <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] bg-[length:20px_20px] animate-[slide_1s_linear_infinite]" data-id="element-775" />
            </div>
          </div>
        </div>

        <p className="text-blue-200/80 text-center mb-10 italic font-sans" data-id="element-776">
          "Esto significa que tienes el perfil ideal para usar el Sistema
          LPM™."
        </p>

        <button onClick={onComplete} className="w-full py-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-[0_0_25px_rgba(59,130,246,0.5)] hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] transition-all flex justify-center items-center gap-3 transform hover:-translate-y-1" data-id="element-777">
          <Zap className="w-6 h-6 animate-pulse" data-id="element-778" />
          <span className="tracking-wider" data-id="element-779">
            Ver cómo crear una web en minutos
          </span>
        </button>
      </div>
    </div>;
}
function Step6HowItWorks({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const [titleDone, setTitleDone] = useState(false);
  const [intro1Done, setIntro1Done] = useState(false);
  const [intro2Done, setIntro2Done] = useState(false);
  const [card1TitleDone, setCard1TitleDone] = useState(false);
  const [card1TextDone, setCard1TextDone] = useState(false);
  const [card2TitleDone, setCard2TitleDone] = useState(false);
  const [card2TextDone, setCard2TextDone] = useState(false);
  const [card3TitleDone, setCard3TitleDone] = useState(false);
  const [card3TextDone, setCard3TextDone] = useState(false);
  const [timeLabelDone, setTimeLabelDone] = useState(false);
  const [timeValueDone, setTimeValueDone] = useState(false);
  const [closingDone, setClosingDone] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  };
  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 500);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (titleDone) {
      const t = setTimeout(() => setPhase(2), 300);
      return () => clearTimeout(t);
    }
  }, [titleDone]);
  useEffect(() => {
    if (intro1Done) {
      const t = setTimeout(() => setPhase(3), 200);
      return () => clearTimeout(t);
    }
  }, [intro1Done]);
  useEffect(() => {
    if (intro2Done) {
      const t = setTimeout(() => setPhase(4), 400);
      return () => clearTimeout(t);
    }
  }, [intro2Done]);
  useEffect(() => {
    if (card1TitleDone) {
      const t = setTimeout(() => setPhase(5), 100);
      return () => clearTimeout(t);
    }
  }, [card1TitleDone]);
  useEffect(() => {
    if (card1TextDone) {
      const t = setTimeout(() => setPhase(6), 400);
      return () => clearTimeout(t);
    }
  }, [card1TextDone]);
  useEffect(() => {
    if (card2TitleDone) {
      const t = setTimeout(() => setPhase(7), 100);
      return () => clearTimeout(t);
    }
  }, [card2TitleDone]);
  useEffect(() => {
    if (card2TextDone) {
      const t = setTimeout(() => setPhase(8), 400);
      return () => clearTimeout(t);
    }
  }, [card2TextDone]);
  useEffect(() => {
    if (card3TitleDone) {
      const t = setTimeout(() => setPhase(9), 100);
      return () => clearTimeout(t);
    }
  }, [card3TitleDone]);
  useEffect(() => {
    if (card3TextDone) {
      const t = setTimeout(() => setPhase(10), 500);
      return () => clearTimeout(t);
    }
  }, [card3TextDone]);
  useEffect(() => {
    if (timeLabelDone) {
      const t = setTimeout(() => setPhase(11), 200);
      return () => clearTimeout(t);
    }
  }, [timeLabelDone]);
  useEffect(() => {
    if (timeValueDone) {
      const t = setTimeout(() => setPhase(12), 300);
      return () => clearTimeout(t);
    }
  }, [timeValueDone]);
  useEffect(() => {
    if (closingDone) {
      const t = setTimeout(() => setPhase(13), 500);
      return () => clearTimeout(t);
    }
  }, [closingDone]);
  useEffect(() => {
    scrollToBottom();
  }, [phase]);
  return <div ref={containerRef} className="w-full max-w-2xl mx-auto animate-fade-in space-y-6 pb-4" data-id="element-780">
      {/* AI message: Title */}
      {phase >= 1 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-781">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-782">
            <Cpu className="w-4 h-4 text-blue-400" data-id="element-783" />
          </div>
          <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-784">
            <p className="text-cyan-300 font-bold text-lg" data-id="element-785">
              <TypewriterText text="Cómo se crean páginas web en menos de 1 hora" speed={40} onComplete={() => setTitleDone(true)} data-id="element-786" />
            </p>
          </div>
        </div>}

      {/* AI message: Intro text */}
      {phase >= 2 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-787">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-788">
            <Cpu className="w-4 h-4 text-blue-400" data-id="element-789" />
          </div>
          <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-2" data-id="element-790">
            <p className="text-blue-100 leading-relaxed" data-id="element-791">
              <TypewriterText text="Con el Sistema LPM™ no necesitas saber diseño, programación ni marketing." speed={45} onComplete={() => setIntro1Done(true)} data-id="element-792" />
            </p>
            {phase >= 3 && <p className="text-blue-300/70 leading-relaxed text-sm" data-id="element-793">
                <TypewriterText text="Las páginas se crean usando herramientas de inteligencia artificial que hacen la mayor parte del trabajo por ti." speed={40} onComplete={() => setIntro2Done(true)} data-id="element-794" />
              </p>}
          </div>
        </div>}

      {/* AI message: Step 1 */}
      {phase >= 4 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-795">
          <div className="w-8 h-8 rounded-full bg-cyan-900/50 border border-cyan-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-796">
            <MessageCircleIcon className="w-4 h-4 text-cyan-400" data-id="element-797" />
          </div>
          <div className="bg-cyan-950/20 border border-cyan-800/40 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-1 flex-1" data-id="element-798">
            <p className="text-cyan-500 text-xs font-bold tracking-widest uppercase mb-1" data-id="element-799">
              PASO 1
            </p>
            <p className="text-cyan-200 font-bold" data-id="element-800">
              <TypewriterText text="Generar el texto" speed={50} onComplete={() => setCard1TitleDone(true)} data-id="element-801" />
            </p>
            {phase >= 5 && <p className="text-blue-300/70 text-sm leading-relaxed" data-id="element-802">
                <TypewriterText text="ChatGPT escribe automáticamente todos los textos profesionales de la página en menos de 30 segundos." speed={35} onComplete={() => setCard1TextDone(true)} data-id="element-803" />
              </p>}
          </div>
        </div>}

      {/* AI message: Step 2 */}
      {phase >= 6 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-804">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-805">
            <LayoutIcon className="w-4 h-4 text-blue-400" data-id="element-806" />
          </div>
          <div className="bg-blue-950/20 border border-blue-800/40 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-1 flex-1" data-id="element-807">
            <p className="text-blue-500 text-xs font-bold tracking-widest uppercase mb-1" data-id="element-808">
              PASO 2
            </p>
            <p className="text-blue-200 font-bold" data-id="element-809">
              <TypewriterText text="Construir la página" speed={50} onComplete={() => setCard2TitleDone(true)} data-id="element-810" />
            </p>
            {phase >= 7 && <p className="text-blue-300/70 text-sm leading-relaxed" data-id="element-811">
                <TypewriterText text="Pegas el texto en Magic Patterns y la inteligencia artificial crea el diseño completo de la landing page." speed={35} onComplete={() => setCard2TextDone(true)} data-id="element-812" />
              </p>}
          </div>
        </div>}

      {/* AI message: Step 3 */}
      {phase >= 8 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-813">
          <div className="w-8 h-8 rounded-full bg-purple-900/50 border border-purple-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-814">
            <RocketIcon className="w-4 h-4 text-purple-400" data-id="element-815" />
          </div>
          <div className="bg-purple-950/20 border border-purple-800/40 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-1 flex-1" data-id="element-816">
            <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-1" data-id="element-817">
              PASO 3
            </p>
            <p className="text-purple-200 font-bold" data-id="element-818">
              <TypewriterText text="Página lista" speed={50} onComplete={() => setCard3TitleDone(true)} data-id="element-819" />
            </p>
            {phase >= 9 && <p className="text-blue-300/70 text-sm leading-relaxed" data-id="element-820">
                <TypewriterText text="En aproximadamente 30 a 60 minutos tienes una página web profesional lista para entregar a un cliente." speed={35} onComplete={() => setCard3TextDone(true)} data-id="element-821" />
              </p>}
          </div>
        </div>}

      {/* Time highlight - typed out */}
      {phase >= 10 && <div className="relative animate-fade-in-up" data-id="element-822">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-blue-500/10 to-purple-500/5 rounded-2xl blur-xl" data-id="element-823" />
          <div className="relative bg-black/60 border border-blue-500/40 rounded-2xl p-6 text-center backdrop-blur-md overflow-hidden" data-id="element-824">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60" data-id="element-825" />
            <div className="absolute inset-0 overflow-hidden" data-id="element-826">
              <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-827" />
            </div>

            <div className="flex items-center justify-center gap-2 mb-3" data-id="element-828">
              <ClockIcon className="w-5 h-5 text-cyan-400" data-id="element-829" />
              <p className="text-blue-300 text-sm font-bold tracking-widest uppercase" data-id="element-830">
                <TypewriterText text="Tiempo promedio para crear una página" speed={40} onComplete={() => setTimeLabelDone(true)} data-id="element-831" />
              </p>
            </div>

            {phase >= 11 && <p className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-400 mb-4 drop-shadow-[0_0_20px_rgba(34,211,238,0.3)]" data-id="element-832">
                <TypewriterText text="30 – 60 minutos" speed={80} onComplete={() => setTimeValueDone(true)} data-id="element-833" />
              </p>}

            {phase >= 12 && <p className="text-blue-200/70 font-sans text-sm md:text-base" data-id="element-834">
                <TypewriterText text="Ahora imagina cuánto podrías generar si crearas algunas páginas al mes." speed={40} onComplete={() => setClosingDone(true)} data-id="element-835" />
              </p>}
          </div>
        </div>}

      {/* CTA button */}
      {phase >= 13 && <div className="flex justify-center animate-fade-in" data-id="element-836">
          <button onClick={onComplete} className="group relative px-10 py-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/50 rounded-xl overflow-hidden transition-all hover:border-blue-400 hover:shadow-[0_0_30px_rgba(59,130,246,0.3)]" data-id="element-837">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-838" />
            <span className="relative flex items-center gap-2 text-blue-100 font-bold tracking-wider" data-id="element-839">
              Calcular mis ingresos potenciales
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-840" />
            </span>
          </button>
        </div>}
    </div>;
}
function Step7Simulator({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [price, setPrice] = useState(400000);
  const [pages, setPages] = useState(4);
  const total = price * pages;
  return <div className="w-full max-w-lg mx-auto animate-fade-in" data-id="element-841">
      <div className="bg-blue-950/30 border border-purple-500/30 rounded-xl p-6 md:p-8 shadow-[0_0_40px_rgba(168,85,247,0.1)] backdrop-blur-sm" data-id="element-842">
        <h2 className="text-xl md:text-2xl font-bold text-purple-100 mb-8 flex items-center gap-2" data-id="element-843">
          <DollarSign className="text-purple-400" data-id="element-844" /> Simulador de ingresos
        </h2>

        <div className="space-y-8" data-id="element-845">
          <div className="space-y-4" data-id="element-846">
            <label className="block text-blue-200" data-id="element-847">
              ¿Cuánto cobrarías por una página web?
            </label>
            <div className="grid grid-cols-3 gap-2 md:gap-4" data-id="element-848">
              {[200000, 400000, 600000].map(p => <button key={p} onClick={() => setPrice(p)} className={`py-2 md:py-3 rounded border text-sm md:text-base transition-all ${price === p ? 'bg-purple-600/40 border-purple-400 text-white shadow-[0_0_15px_rgba(168,85,247,0.4)]' : 'bg-blue-950/50 border-blue-800/50 text-blue-300 hover:border-purple-500/50'}`} data-id="element-849">
                  ${(p / 1000).toFixed(0)}k
                </button>)}
            </div>
          </div>

          <div className="space-y-4" data-id="element-850">
            <div className="flex justify-between text-blue-200" data-id="element-851">
              <label data-id="element-852">¿Cuántas páginas podrías crear al mes?</label>
              <span className="font-bold text-purple-300" data-id="element-853">{pages}</span>
            </div>
            <input type="range" min="1" max="15" value={pages} onChange={e => setPages(parseInt(e.target.value))} className="w-full h-2 bg-blue-950 rounded-lg appearance-none cursor-pointer accent-purple-500" data-id="element-854" />
            <div className="flex justify-between text-xs text-blue-500" data-id="element-855">
              <span data-id="element-856">1</span>
              <span data-id="element-857">15</span>
            </div>
          </div>

          <div className="p-6 bg-black/50 border border-purple-500/20 rounded-lg text-center relative overflow-hidden" data-id="element-858">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-transparent" data-id="element-859" />
            <p className="text-blue-300 text-sm mb-2 relative z-10" data-id="element-860">
              Ingreso mensual estimado
            </p>
            <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 relative z-10" data-id="element-861">
              ${total.toLocaleString('es-CO')} COP
            </p>
          </div>

          <div className="text-center space-y-4 pt-4 border-t border-blue-900/50" data-id="element-862">
            <p className="text-blue-200/70 text-sm" data-id="element-863">
              Esto es exactamente lo que enseña el Sistema LPM™.
            </p>
            <button onClick={onComplete} className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-lg shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all" data-id="element-864">
              Ver cómo funciona el sistema
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function Step8ClientSim({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const [d, setD] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const done = (key: string) => setD(prev => ({
    ...prev,
    [key]: true
  }));
  const scrollToBottom = () => {
    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  };
  // Phase progression
  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 500);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (d.title) {
      const t = setTimeout(() => setPhase(2), 400);
      return () => clearTimeout(t);
    }
  }, [d.title]);
  useEffect(() => {
    if (d.exp1) {
      const t = setTimeout(() => setPhase(3), 300);
      return () => clearTimeout(t);
    }
  }, [d.exp1]);
  useEffect(() => {
    if (d.exp2) {
      const t = setTimeout(() => setPhase(4), 300);
      return () => clearTimeout(t);
    }
  }, [d.exp2]);
  useEffect(() => {
    if (d.exp3) {
      const t = setTimeout(() => setPhase(5), 600);
      return () => clearTimeout(t);
    }
  }, [d.exp3]);
  useEffect(() => {
    if (d.biz) {
      const t = setTimeout(() => setPhase(6), 500);
      return () => clearTimeout(t);
    }
  }, [d.biz]);
  useEffect(() => {
    if (d.analyze) {
      const t = setTimeout(() => setPhase(7), 300);
      return () => clearTimeout(t);
    }
  }, [d.analyze]);
  useEffect(() => {
    if (d.prompt) {
      const t = setTimeout(() => setPhase(8), 500);
      return () => clearTimeout(t);
    }
  }, [d.prompt]);
  useEffect(() => {
    if (d.gen1) {
      const t = setTimeout(() => setPhase(9), 300);
      return () => clearTimeout(t);
    }
  }, [d.gen1]);
  useEffect(() => {
    if (d.gen2) {
      const t = setTimeout(() => setPhase(10), 300);
      return () => clearTimeout(t);
    }
  }, [d.gen2]);
  // Auto-advance from chat header (phase 10) to first message (phase 11)
  useEffect(() => {
    if (phase === 10) {
      const t = setTimeout(() => setPhase(11), 800);
      return () => clearTimeout(t);
    }
  }, [phase]);
  useEffect(() => {
    if (d.msg1) {
      const t = setTimeout(() => setPhase(12), 1200);
      return () => clearTimeout(t);
    }
  }, [d.msg1]);
  useEffect(() => {
    if (d.typing1) {
      const t = setTimeout(() => setPhase(13), 2000);
      return () => clearTimeout(t);
    }
  }, [d.typing1]);
  useEffect(() => {
    if (d.reply1) {
      const t = setTimeout(() => setPhase(14), 800);
      return () => clearTimeout(t);
    }
  }, [d.reply1]);
  useEffect(() => {
    if (d.msg2) {
      const t = setTimeout(() => setPhase(15), 1200);
      return () => clearTimeout(t);
    }
  }, [d.msg2]);
  useEffect(() => {
    if (d.reply2) {
      const t = setTimeout(() => setPhase(16), 800);
      return () => clearTimeout(t);
    }
  }, [d.reply2]);
  useEffect(() => {
    if (d.msg3) {
      const t = setTimeout(() => setPhase(17), 1200);
      return () => clearTimeout(t);
    }
  }, [d.msg3]);
  // Phase 12 auto-triggers typing indicator 1
  useEffect(() => {
    if (phase === 12) {
      done('typing1');
    }
  }, [phase]);
  useEffect(() => {
    scrollToBottom();
  }, [phase]);
  return <div ref={containerRef} className="w-full max-w-2xl mx-auto animate-fade-in space-y-5 pb-4" data-id="element-865">
      {/* ── SECTION TITLE ── */}
      {phase >= 1 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-866">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-867">
            <Cpu className="w-4 h-4 text-blue-400" data-id="element-868" />
          </div>
          <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-869">
            <p className="text-cyan-300 font-bold text-lg" data-id="element-870">
              <TypewriterText text="Cómo funciona el Sistema LPM™" speed={45} onComplete={() => done('title')} data-id="element-871" />
            </p>
          </div>
        </div>}

      {/* ── STEP 1: AI EXPLANATION ── */}
      {phase >= 2 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-872">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-873">
            <Cpu className="w-4 h-4 text-blue-400" data-id="element-874" />
          </div>
          <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-3" data-id="element-875">
            <p className="text-blue-100 leading-relaxed" data-id="element-876">
              <TypewriterText text="Lo que aprenderás con el Sistema LPM™ es a encontrar negocios que necesitan una landing page para conseguir más clientes." speed={40} onComplete={() => done('exp1')} data-id="element-877" />
            </p>
            {phase >= 3 && <p className="text-blue-300/70 leading-relaxed text-sm" data-id="element-878">
                <TypewriterText text="Cada día miles de negocios en Colombia tienen redes sociales… pero no tienen una página web que convierta visitantes en clientes." speed={38} onComplete={() => done('exp2')} data-id="element-879" />
              </p>}
            {phase >= 4 && <p className="text-blue-100 font-bold" data-id="element-880">
                <TypewriterText text="El proceso es simple." speed={55} onComplete={() => done('exp3')} data-id="element-881" />
              </p>}
          </div>
        </div>}

      {/* ── STEP 2: BUSINESS DETECTED ── */}
      {phase >= 5 && <div className="animate-fade-in-up space-y-4" data-id="element-882">
          {/* Detection panel */}
          <div className="relative bg-blue-950/30 border border-cyan-500/30 rounded-xl p-5 backdrop-blur-sm overflow-hidden" data-id="element-883">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-50" data-id="element-884" />
            <div className="flex items-center gap-2 mb-3" data-id="element-885">
              <SearchIcon className="w-4 h-4 text-cyan-400" data-id="element-886" />
              <span className="text-cyan-400 text-xs font-bold tracking-widest uppercase" data-id="element-887">
                Negocio detectado
              </span>
            </div>
            <div className="flex items-center gap-4" data-id="element-888">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-900/60 to-purple-900/60 border border-blue-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.2)]" data-id="element-889">
                <ScissorsIcon className="w-7 h-7 text-blue-300" data-id="element-890" />
              </div>
              <div data-id="element-891">
                <h3 className="text-blue-100 font-bold text-lg" data-id="element-892">
                  BarberShop Medellín
                </h3>
                <div className="flex items-center gap-3 mt-1" data-id="element-893">
                  <div className="flex items-center gap-1 text-pink-400/70 text-xs" data-id="element-894">
                    <GlobeIcon className="w-3 h-3" data-id="element-895" /> Instagram
                  </div>
                  <div className="flex items-center gap-1 text-blue-400/70 text-xs" data-id="element-896">
                    <MapPinIcon className="w-3 h-3" data-id="element-897" /> Google Maps
                  </div>
                  <div className="flex items-center gap-1 text-blue-300/70 text-xs" data-id="element-898">
                    <GlobeIcon className="w-3 h-3" data-id="element-899" /> Facebook
                  </div>
                </div>
              </div>
            </div>
            {/* Scanning effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-900">
              <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-901" />
            </div>
            <p className="text-xs text-red-400/80 mt-3 font-bold" data-id="element-902">
              ⚠ No tiene página web
            </p>
          </div>

          {/* AI analysis messages */}
          <div className="flex gap-4 items-start" data-id="element-903">
            <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-904">
              <Cpu className="w-4 h-4 text-blue-400" data-id="element-905" />
            </div>
            <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-2" data-id="element-906">
              <p className="text-blue-100 leading-relaxed text-sm" data-id="element-907">
                <TypewriterText text="Analizamos la información del negocio en redes sociales y Google." speed={40} onComplete={() => done('biz')} data-id="element-908" />
              </p>
              {phase >= 6 && <p className="text-blue-300/70 leading-relaxed text-sm" data-id="element-909">
                  <TypewriterText text="Con esa información generamos un prompt para ChatGPT." speed={40} onComplete={() => done('analyze')} data-id="element-910" />
                </p>}
            </div>
          </div>
        </div>}

      {/* ── STEP 3: AI GENERATING PAGE ── */}
      {phase >= 7 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-911">
          <div className="w-8 h-8 rounded-full bg-purple-900/50 border border-purple-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-912">
            <SparklesIcon className="w-4 h-4 text-purple-400" data-id="element-913" />
          </div>
          <div className="bg-purple-950/20 border border-purple-800/40 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm space-y-2" data-id="element-914">
            <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-1" data-id="element-915">
              GENERACIÓN CON IA
            </p>
            <p className="text-blue-100 leading-relaxed text-sm" data-id="element-916">
              <TypewriterText text="ChatGPT genera el texto de venta para la página." speed={40} onComplete={() => done('prompt')} data-id="element-917" />
            </p>
            {phase >= 8 && <p className="text-blue-300/70 leading-relaxed text-sm" data-id="element-918">
                <TypewriterText text="El texto se pega en Magic Patterns." speed={40} onComplete={() => done('gen1')} data-id="element-919" />
              </p>}
            {phase >= 9 && <p className="text-blue-100 font-bold text-sm" data-id="element-920">
                <TypewriterText text="En menos de una hora se crea una landing page profesional." speed={38} onComplete={() => done('gen2')} data-id="element-921" />
              </p>}
          </div>
        </div>}

      {/* ── STEP 4: CHAT WITH BUSINESS ── */}
      {phase >= 10 && <div className="animate-fade-in-up" data-id="element-922">
          <div className="bg-[#0d1520] border border-blue-900/50 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.6)]" data-id="element-923">
            {/* Chat header */}
            <div className="bg-blue-950/50 border-b border-blue-900/50 p-4 flex items-center gap-3" data-id="element-924">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-800 to-purple-900 rounded-full flex items-center justify-center border border-blue-600/50 shadow-[0_0_10px_rgba(59,130,246,0.3)]" data-id="element-925">
                <ScissorsIcon className="w-5 h-5 text-blue-200" data-id="element-926" />
              </div>
              <div data-id="element-927">
                <h3 className="text-blue-100 font-bold" data-id="element-928">BarberShop Medellín</h3>
                <p className="text-green-400 text-xs flex items-center gap-1" data-id="element-929">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-930" />{' '}
                  En línea
                </p>
              </div>
            </div>

            {/* Chat messages */}
            <div className="p-4 space-y-4 min-h-[200px]" data-id="element-931">
              {/* Your first message */}
              {phase >= 11 && <div className="bg-blue-600/20 border border-blue-500/40 text-blue-100 p-3 rounded-2xl rounded-tr-none max-w-[90%] self-end ml-auto animate-fade-in-up text-sm leading-relaxed" data-id="element-932">
                  <TypewriterText text="Hola, estuve revisando su barbería y noté que muchas personas buscan servicios como los suyos en Google. Creé una página web profesional para que sus clientes tomen más fácil los servicios. Aquí se la dejo para que la mire a ver si le interesa." speed={32} onComplete={() => done('msg1')} data-id="element-933" />
                </div>}

              {/* Link message */}
              {phase >= 11 && d.msg1 && <div className="bg-blue-600/20 border border-blue-500/40 text-blue-100 p-3 rounded-2xl rounded-tr-none max-w-[85%] self-end ml-auto animate-fade-in-up text-sm" data-id="element-934">
                  <span className="text-cyan-400 underline underline-offset-2 cursor-default" onClick={e => e.preventDefault()} data-id="element-935">
                    www.barbershop.com
                  </span>
                </div>}

              {/* Typing indicator 1 */}
              {phase === 12 && <div className="bg-gray-800/60 border border-gray-700/50 p-3 rounded-2xl rounded-tl-none max-w-[30%] self-start animate-fade-in-up" data-id="element-936">
                  <div className="flex gap-1.5 items-center" data-id="element-937">
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-938" />
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-939" />
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-940" />
                  </div>
                </div>}

              {/* Owner reply 1 */}
              {phase >= 13 && <div className="bg-gray-800/60 border border-gray-700/50 text-gray-200 p-3 rounded-2xl rounded-tl-none max-w-[85%] self-start animate-fade-in-up text-sm" data-id="element-941">
                  <TypewriterText text="Hola, claro. Me interesa verla." speed={50} onComplete={() => done('reply1')} data-id="element-942" />
                </div>}

              {/* Your second message */}
              {phase >= 14 && <div className="bg-blue-600/20 border border-blue-500/40 text-blue-100 p-3 rounded-2xl rounded-tr-none max-w-[85%] self-end ml-auto animate-fade-in-up text-sm" data-id="element-943">
                  <TypewriterText text="Perfecto. Si le gusta, puedo entregársela hoy mismo." speed={45} onComplete={() => done('msg2')} data-id="element-944" />
                </div>}

              {/* Owner reply 2 */}
              {phase >= 15 && <div className="bg-gray-800/60 border border-gray-700/50 text-gray-200 p-3 rounded-2xl rounded-tl-none max-w-[85%] self-start animate-fade-in-up text-sm" data-id="element-945">
                  <TypewriterText text="Genial. ¿Cuánto cuesta?" speed={50} onComplete={() => done('reply2')} data-id="element-946" />
                </div>}

              {/* Your price reply */}
              {phase >= 16 && <div className="bg-blue-600/20 border border-blue-500/40 text-blue-100 p-3 rounded-2xl rounded-tr-none max-w-[85%] self-end ml-auto animate-fade-in-up text-sm font-bold" data-id="element-947">
                  <TypewriterText text="$400.000" speed={80} onComplete={() => done('msg3')} data-id="element-948" />
                </div>}
            </div>

            {/* ── METRICS + CTA (inside chat container) ── */}
            {phase >= 17 && <div className="p-5 border-t border-blue-500/30 animate-fade-in bg-blue-950/40" data-id="element-949">
                <div className="grid grid-cols-2 gap-4 mb-5" data-id="element-950">
                  <div className="bg-black/40 border border-blue-500/30 p-4 rounded-xl text-center relative overflow-hidden" data-id="element-951">
                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-400/50 to-transparent" data-id="element-952" />
                    <p className="text-blue-400 text-xs mb-1.5 font-bold tracking-wider uppercase" data-id="element-953">
                      Tiempo de creación
                    </p>
                    <p className="text-blue-100 font-mono font-bold text-xl drop-shadow-[0_0_8px_rgba(59,130,246,0.4)]" data-id="element-954">
                      47 minutos
                    </p>
                  </div>
                  <div className="bg-black/40 border border-green-500/30 p-4 rounded-xl text-center relative overflow-hidden" data-id="element-955">
                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-green-400/50 to-transparent" data-id="element-956" />
                    <p className="text-green-400 text-xs mb-1.5 font-bold tracking-wider uppercase" data-id="element-957">
                      Ganancia obtenida
                    </p>
                    <p className="text-green-100 font-mono font-bold text-xl drop-shadow-[0_0_8px_rgba(74,222,128,0.4)]" data-id="element-958">
                      $400.000
                    </p>
                  </div>
                </div>

                <p className="text-center text-blue-200/80 text-sm mb-5" data-id="element-959">
                  Este es exactamente el proceso que aprenderás con el Sistema
                  LPM™.
                </p>

                <button onClick={onComplete} className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-[0_0_25px_rgba(59,130,246,0.4)] hover:shadow-[0_0_35px_rgba(168,85,247,0.5)] transition-all transform hover:-translate-y-0.5" data-id="element-960">
                  Acceder al Sistema LPM™
                </button>
              </div>}
          </div>
        </div>}
    </div>;
}
function Step9Testimonials({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [phase, setPhase] = useState(0);
  const [titleDone, setTitleDone] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstTestimonialRef = useRef<HTMLDivElement>(null);
  // Start typewriter
  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 500);
    return () => clearTimeout(t);
  }, []);
  // After typewriter → show video
  useEffect(() => {
    if (titleDone) {
      const t = setTimeout(() => setPhase(2), 600);
      return () => clearTimeout(t);
    }
  }, [titleDone]);
  // Auto-play video when it appears
  useEffect(() => {
    if (phase === 2 && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked — user can tap play
      });
    }
  }, [phase]);
  // Scroll to bottom on phase change
  useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  }, [phase]);
  // Scroll to first testimonial when they appear
  useEffect(() => {
    if (phase === 3) {
      setTimeout(() => {
        firstTestimonialRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 200);
    }
  }, [phase]);
  const handleVideoEnd = () => {
    setPhase(3);
  };
  return <div ref={containerRef} className="w-full max-w-2xl mx-auto animate-fade-in space-y-5 pb-4" data-id="element-961">
      {/* AI System message with typewriter */}
      {phase >= 1 && <div className="flex gap-4 items-start animate-fade-in-up" data-id="element-962">
          <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-500/30 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-963">
            <Cpu className="w-4 h-4 text-blue-400" data-id="element-964" />
          </div>
          <div className="bg-blue-950/40 border border-blue-800/50 p-4 rounded-2xl rounded-tl-none shadow-[0_4px_20px_rgba(0,0,0,0.3)] backdrop-blur-sm" data-id="element-965">
            <p className="text-blue-100 leading-relaxed" data-id="element-966">
              <TypewriterText text="Mira lo que dicen personas que ya lo están utilizando." speed={45} onComplete={() => setTitleDone(true)} data-id="element-967" />
            </p>
          </div>
        </div>}

      {/* Video — inline in chat, auto-plays */}
      {phase >= 2 && <div className="animate-fade-in-up" data-id="element-968">
          <div className="rounded-xl overflow-hidden border border-blue-800/40 shadow-[0_0_20px_rgba(59,130,246,0.15)] bg-black/40" data-id="element-969">
            <video ref={videoRef} controls playsInline webkit-playsinline="true" preload="none" onEnded={handleVideoEnd} className="w-full h-auto" style={{
          maxHeight: '60vh',
          objectFit: 'contain'
        }} data-id="element-970">
              <source src="https://ydgkmdmvhrniztrhtrxq.supabase.co/storage/v1/object/sign/audio%20receta/freepik_1080x1920_30fps_16352.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80NDNkYTY1ZS1iYWM1LTQyYWQtODMzNS1iNWE4N2M0NDYxNmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyByZWNldGEvZnJlZXBpa18xMDgweDE5MjBfMzBmcHNfMTYzNTIubXA0IiwiaWF0IjoxNzcyNjcwMTAwLCJleHAiOjE4MDQyMDYxMDB9.b9wCY-opwUlDoEsHLJbYByugwDoMkIqeD53MAh0SX5Y#t=0.1" type="video/mp4" data-id="element-971" />
              Tu navegador no soporta la etiqueta de video.
            </video>
          </div>
        </div>}

      {/* Testimonials — appear after video ends */}
      {phase >= 3 && <div className="space-y-4 animate-fade-in-up" data-id="element-972">
          {/* Testimonial 1 */}
          <div ref={firstTestimonialRef} className="bg-blue-950/30 border border-blue-800/40 rounded-xl p-5 backdrop-blur-sm" data-id="element-973">
            <div className="flex items-center gap-3 mb-3" data-id="element-974">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face" alt="Carlos M." className="w-12 h-12 rounded-full object-cover border-2 border-blue-500/40" loading="lazy" decoding="async" width="48" height="48" data-id="element-975" />
              <div data-id="element-976">
                <p className="text-blue-100 font-bold text-sm" data-id="element-977">Carlos M.</p>
                <p className="text-blue-400/60 text-xs" data-id="element-978">Medellín, Colombia</p>
              </div>
            </div>
            <p className="text-blue-200/80 text-sm leading-relaxed italic" data-id="element-979">
              "Nunca había hecho una página web en mi vida. Con el sistema LPM™
              creé mi primera landing en 45 minutos y la vendí por $350.000. Ya
              llevo 3 meses facturando más de 3 millones mensuales."
            </p>
            <div className="flex items-center gap-1 mt-3" data-id="element-980">
              {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-sm" data-id="element-981">
                  ★
                </span>)}
            </div>
          </div>

          {/* Testimonial 2 */}
          <div className="bg-blue-950/30 border border-blue-800/40 rounded-xl p-5 backdrop-blur-sm" data-id="element-982">
            <div className="flex items-center gap-3 mb-3" data-id="element-983">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face" alt="Laura G." className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/40" loading="lazy" decoding="async" width="48" height="48" data-id="element-984" />
              <div data-id="element-985">
                <p className="text-blue-100 font-bold text-sm" data-id="element-986">Laura G.</p>
                <p className="text-blue-400/60 text-xs" data-id="element-987">Bogotá, Colombia</p>
              </div>
            </div>
            <p className="text-blue-200/80 text-sm leading-relaxed italic" data-id="element-988">
              "Soy mamá de dos hijos y buscaba algo que pudiera hacer desde
              casa. El sistema me enseñó paso a paso. Hoy creo 5-6 páginas al
              mes y genero entre 2 y 4 millones. Es increíble."
            </p>
            <div className="flex items-center gap-1 mt-3" data-id="element-989">
              {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-sm" data-id="element-990">
                  ★
                </span>)}
            </div>
          </div>

          {/* Testimonial 3 */}
          <div className="bg-blue-950/30 border border-blue-800/40 rounded-xl p-5 backdrop-blur-sm" data-id="element-991">
            <div className="flex items-center gap-3 mb-3" data-id="element-992">
              <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face" alt="Andrés R." className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500/40" loading="lazy" decoding="async" width="48" height="48" data-id="element-993" />
              <div data-id="element-994">
                <p className="text-blue-100 font-bold text-sm" data-id="element-995">Andrés R.</p>
                <p className="text-blue-400/60 text-xs" data-id="element-996">Cali, Colombia</p>
              </div>
            </div>
            <p className="text-blue-200/80 text-sm leading-relaxed italic" data-id="element-997">
              "Dejé mi trabajo de oficina hace 4 meses. Con el Sistema LPM™
              aprendí a crear páginas con IA y ahora facturo 6 millones al mes
              trabajando 3-4 horas diarias. La mejor inversión que he hecho."
            </p>
            <div className="flex items-center gap-1 mt-3" data-id="element-998">
              {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-sm" data-id="element-999">
                  ★
                </span>)}
            </div>
          </div>

          {/* CTA */}
          <button onClick={onComplete} className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-[0_0_25px_rgba(59,130,246,0.4)] hover:shadow-[0_0_35px_rgba(168,85,247,0.5)] transition-all transform hover:-translate-y-0.5 mt-4" data-id="element-1000">
            Acceder al Sistema LPM™
          </button>
        </div>}
    </div>;
}
function Step10Offer() {
  const counterValues = [400000, 800000, 1200000, 1600000, 2000000, 2400000, 2800000, 3200000, 3600000, 4000000, 4400000, 4800000, 5200000, 5600000, 6000000];
  const [counterIdx, setCounterIdx] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Scroll to top of this section so user sees the money counter first
    setTimeout(() => {
      topRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 200);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setCounterIdx(prev => (prev + 1) % counterValues.length);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const displayValue = `${counterValues[counterIdx].toLocaleString('es-CO')}`;
  return <div ref={topRef} className="w-full max-w-xl mx-auto animate-fade-in pb-10" data-id="element-1001">
      <div className="relative border border-blue-500/50 rounded-2xl p-1 overflow-hidden" style={{
      backgroundColor: '#0f172a'
    }} data-id="element-1002">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 opacity-30 animate-[spin_4s_linear_infinite]" style={{
        margin: '-50%'
      }} data-id="element-1003" />

        <div className="relative bg-[#0f172a] rounded-xl p-6 md:p-10 z-10" data-id="element-1004">
          {/* Futuristic Money Counter Animation */}
          <div className="relative w-full h-48 md:h-56 mb-8 flex items-center justify-center overflow-hidden" data-id="element-1005">
            {/* Ambient glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-green-500/10 to-transparent rounded-xl" data-id="element-1006" />

            {/* Machine body */}
            <div className="relative w-52 md:w-64" data-id="element-1007">
              {/* Top slot (bills enter) */}
              <div className="relative mx-auto w-40 md:w-48 h-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 rounded-t-lg border border-cyan-500/30 overflow-hidden" data-id="element-1008">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent animate-[scan_2s_ease-in-out_infinite]" data-id="element-1009" />
              </div>

              {/* Machine main body */}
              <div className="relative mx-auto w-44 md:w-52 bg-gradient-to-b from-gray-900 via-[#0a1628] to-gray-900 border border-cyan-500/20 rounded-lg p-3 shadow-[0_0_30px_rgba(34,211,238,0.1),inset_0_0_20px_rgba(0,0,0,0.5)]" data-id="element-1010">
                {/* Screen */}
                <div className="bg-black/80 border border-green-500/30 rounded-md p-2 mb-2 relative overflow-hidden" data-id="element-1011">
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,0,0.03)_1px,transparent_1px)] bg-[size:100%_3px]" data-id="element-1012" />
                  <p className="text-green-400 font-mono text-xs text-center animate-pulse drop-shadow-[0_0_8px_rgba(74,222,128,0.8)]" data-id="element-1013">
                    CONTANDO...
                  </p>
                  <p key={counterIdx} className="text-green-300 font-mono text-lg md:text-xl text-center font-bold drop-shadow-[0_0_12px_rgba(74,222,128,0.6)] animate-fade-in" data-id="element-1014">
                    {displayValue}
                  </p>
                </div>

                {/* Bill rollers */}
                <div className="flex items-center justify-center gap-1 my-2" data-id="element-1015">
                  <div className="w-3 h-3 rounded-full border border-cyan-500/40 bg-gray-800 animate-[spin_1s_linear_infinite]" data-id="element-1016">
                    <div className="w-full h-[1px] bg-cyan-400/50 mt-[5px]" data-id="element-1017" />
                  </div>
                  <div className="flex-1 h-[2px] bg-gradient-to-r from-cyan-500/20 via-cyan-400/40 to-cyan-500/20 relative overflow-hidden" data-id="element-1018">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/60 to-transparent animate-[slide_0.5s_linear_infinite]" data-id="element-1019" />
                  </div>
                  <div className="w-3 h-3 rounded-full border border-cyan-500/40 bg-gray-800 animate-[spin_1s_linear_infinite]" data-id="element-1020">
                    <div className="w-full h-[1px] bg-cyan-400/50 mt-[5px]" data-id="element-1021" />
                  </div>
                </div>

                {/* Side LEDs */}
                <div className="absolute top-3 left-1 flex flex-col gap-2" data-id="element-1022">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.8)] animate-pulse" data-id="element-1023" />
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.8)] animate-[pulse_1.5s_ease-in-out_infinite_0.5s]" data-id="element-1024" />
                </div>
                <div className="absolute top-3 right-1 flex flex-col gap-2" data-id="element-1025">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.8)] animate-[pulse_1.5s_ease-in-out_infinite_0.3s]" data-id="element-1026" />
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.8)] animate-pulse" data-id="element-1027" />
                </div>
              </div>

              {/* Bottom slot (bills exit) */}
              <div className="relative mx-auto w-40 md:w-48 h-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 rounded-b-lg border border-cyan-500/30 overflow-hidden" data-id="element-1028">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/20 to-transparent animate-[scan_1.5s_ease-in-out_infinite_0.5s]" data-id="element-1029" />
              </div>
            </div>

            {/* Flying bills - entering from top */}
            {[0, 1, 2].map(i => <div key={`bill-in-${i}`} className="absolute" style={{
            top: '-10px',
            left: `calc(50% - 20px + ${(i - 1) * 8}px)`,
            animation: `billFeedIn 1.2s ease-in infinite ${i * 0.4}s`
          }} data-id="element-1030">
                <div className="w-10 h-5 bg-gradient-to-r from-green-800 to-green-600 rounded-sm border border-green-400/30 flex items-center justify-center shadow-[0_0_8px_rgba(74,222,128,0.3)]" data-id="element-1031">
                  <span className="text-green-200 text-[8px] font-bold" data-id="element-1032">
                    $$
                  </span>
                </div>
              </div>)}

            {/* Flying bills - exiting from bottom */}
            {[0, 1, 2].map(i => <div key={`bill-out-${i}`} className="absolute" style={{
            bottom: '10px',
            left: `calc(50% - 20px + ${(i - 1) * 10}px)`,
            animation: `billFeedOut 1.2s ease-out infinite ${i * 0.4 + 0.6}s`,
            opacity: 0
          }} data-id="element-1033">
                <div className="w-10 h-5 bg-gradient-to-r from-green-700 to-green-500 rounded-sm border border-green-300/40 flex items-center justify-center shadow-[0_0_12px_rgba(74,222,128,0.4)]" data-id="element-1034">
                  <span className="text-green-100 text-[8px] font-bold" data-id="element-1035">
                    $$
                  </span>
                </div>
              </div>)}

            {/* Particle sparks */}
            {[...Array(6)].map((_, i) => <div key={`spark-${i}`} className="absolute w-1 h-1 rounded-full bg-cyan-400/60" style={{
            left: `${40 + Math.random() * 20}%`,
            top: `${30 + Math.random() * 40}%`,
            animation: `particleFloat ${1.5 + Math.random() * 2}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`
          }} data-id="element-1036" />)}
          </div>

          <div className="text-center mb-8" data-id="element-1037">
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-2" data-id="element-1038">
              Sistema LPM™
            </h2>
            <p className="text-blue-200/80" data-id="element-1039">
              La guía paso a paso para generar ingresos creando páginas web con
              inteligencia artificial.
            </p>
          </div>

          <div className="space-y-3 mb-8" data-id="element-1040">
            {['Guía Maestra LPM™ paso a paso', 'Librería de prompts para ChatGPT', 'Protocolo de construcción de páginas en 60 minutos', 'Scripts para conseguir clientes en Colombia', 'Plan de acción de 30 días', 'Plantilla profesional de propuesta'].map((item, i) => <div key={i} className="flex items-start gap-3 text-blue-100" data-id="element-1041">
                <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1042" />
                <span data-id="element-1043">{item}</span>
              </div>)}
          </div>

          <div className="bg-blue-950/30 border border-blue-900/50 rounded-lg p-6 text-center mb-6 relative overflow-hidden" data-id="element-1044">
            <div className="absolute top-0 right-0 bg-red-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg" data-id="element-1045">
              OFERTA DE LANZAMIENTO
            </div>
            <p className="text-blue-300/60 line-through mb-1" data-id="element-1046">
              Valor total: $195.000
            </p>
            <p className="text-4xl font-bold text-white mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" data-id="element-1047">
              Precio hoy: $49.999
            </p>
            <p className="text-red-400 text-sm font-bold animate-pulse" data-id="element-1048">
              Solo para las primeras 300 personas.
            </p>
            {/* Payment methods */}
            <div className="mt-4 flex justify-center" data-id="element-1049">
              <img src="https://cdn.magicpatterns.com/uploads/qp5TjajiqF5LEVHevMY9pY/image.png" alt="Formas de pago: Nequi, PSE, Bancolombia" className="h-10 md:h-12 object-contain" loading="lazy" data-id="element-1050" />
            </div>
          </div>

          <a href="https://www.tiendasamazonica.com/cart/50963070517533:1" target="_blank" rel="noopener noreferrer" onClick={() => {
          // Fire Meta Pixel InitiateCheckout event
          if (typeof window !== 'undefined' && (window as any).fbq) {
            ;
            (window as any).fbq('track', 'InitiateCheckout', {
              content_name: 'LPM™ System Guide',
              content_ids: ['50963070517533'],
              content_type: 'product',
              value: 49999,
              currency: 'COP'
            });
          }
        }} className="block w-full py-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-center font-bold text-lg rounded-lg shadow-[0_0_30px_rgba(59,130,246,0.5)] hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] transition-all transform hover:-translate-y-1" data-id="element-1051">
            Descargar el Sistema LPM™ ahora
          </a>

          <p className="text-center text-blue-400/60 text-xs mt-4 flex items-center justify-center gap-2" data-id="element-1052">
            <span data-id="element-1053">Acceso inmediato</span>
            <span data-id="element-1054">•</span>
            <span data-id="element-1055">Descarga digital</span>
            <span data-id="element-1056">•</span>
            <span data-id="element-1057">Garantía de 30 días</span>
          </p>
        </div>
      </div>
    </div>;
}